import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const galleryImages = [
  {
    id: 1,
    src: "/gallery-couples-room.jpg",
    title: "Couples Massage Room",
    description: "Our beautiful couples massage room with tropical sunset ambiance"
  },
  {
    id: 2,
    src: "/gallery-romantic.jpg",
    title: "Romantic Setting",
    description: "Side-by-side massage tables for the perfect couples experience"
  },
  {
    id: 3,
    src: "/gallery-professional.jpg",
    title: "Professional Setup",
    description: "Clean, professional massage environment"
  },
  {
    id: 4,
    src: "/gallery-treatment.jpg",
    title: "Treatment Room",
    description: "Warm and inviting treatment space"
  },
  {
    id: 5,
    src: "https://customer-assets.emergentagent.com/job_4d2f52c7-22cd-4150-9059-ad99d56ca7fb/artifacts/ngbssild_IMG-20260713-WA0001.jpg",
    title: "Thai Massage with Rino",
    description: "Rino performing a traditional Thai back and hip stretch"
  },
  {
    id: 6,
    src: "https://customer-assets.emergentagent.com/job_4d2f52c7-22cd-4150-9059-ad99d56ca7fb/artifacts/u5qabor3_IMG-20260713-WA0002.jpg",
    title: "Thai Yoga Stretching",
    description: "Deep assisted stretching by our certified Thai massage professional Rino"
  }
];

const GallerySection = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setSelectedImage(galleryImages[index]);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const goToPrevious = () => {
    const newIndex = currentIndex === 0 ? galleryImages.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    setSelectedImage(galleryImages[newIndex]);
  };

  const goToNext = () => {
    const newIndex = currentIndex === galleryImages.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    setSelectedImage(galleryImages[newIndex]);
  };

  return (
    <section id="gallery" className="py-24 bg-gradient-to-b from-stone-50 to-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-sage-100/30 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-warm-100/30 rounded-full blur-3xl" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block text-sage-600 font-medium tracking-wider uppercase text-sm mb-4">
            Our Space
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-stone-800 mb-6">
            Experience Our
            <span className="block text-sage-600">Sanctuary</span>
          </h2>
          <p className="text-stone-600 text-lg">
            Step into our tranquil treatment rooms designed for your ultimate relaxation
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Large featured image */}
          <div 
            className="md:row-span-2 relative group cursor-pointer overflow-hidden rounded-3xl shadow-lg"
            onClick={() => openLightbox(0)}
          >
            <img 
              src={galleryImages[0].src}
              alt={galleryImages[0].title}
              className="w-full h-full min-h-[400px] object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-full group-hover:translate-y-0 transition-transform duration-500">
              <h3 className="text-xl font-semibold mb-2">{galleryImages[0].title}</h3>
              <p className="text-white/80 text-sm">{galleryImages[0].description}</p>
            </div>
            <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm opacity-0 group-hover:opacity-100 transition-opacity">
              Click to enlarge
            </div>
          </div>

          {/* Smaller images */}
          {galleryImages.slice(1).map((image, index) => (
            <div 
              key={image.id}
              className="relative group cursor-pointer overflow-hidden rounded-3xl shadow-lg"
              onClick={() => openLightbox(index + 1)}
            >
              <img 
                src={image.src}
                alt={image.title}
                className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="absolute bottom-0 left-0 right-0 p-4 text-white transform translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                <h3 className="text-lg font-semibold">{image.title}</h3>
                <p className="text-white/80 text-sm">{image.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Ambient text */}
        <div className="text-center mt-12">
          <p className="text-stone-500 italic">
            "Where relaxation meets luxury"
          </p>
        </div>
      </div>

      {/* Lightbox */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white/70 hover:text-white p-2 transition-colors z-10"
            data-testid="close-gallery-lightbox"
          >
            <X size={32} />
          </button>

          {/* Navigation */}
          <button
            onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
            className="absolute left-4 text-white/70 hover:text-white p-2 transition-colors bg-white/10 hover:bg-white/20 rounded-full"
          >
            <ChevronLeft size={32} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); goToNext(); }}
            className="absolute right-4 text-white/70 hover:text-white p-2 transition-colors bg-white/10 hover:bg-white/20 rounded-full"
          >
            <ChevronRight size={32} />
          </button>

          {/* Image */}
          <div 
            className="max-w-5xl max-h-[85vh] relative"
            onClick={(e) => e.stopPropagation()}
          >
            <img 
              src={selectedImage.src}
              alt={selectedImage.title}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
            <div className="text-center mt-4 text-white">
              <h3 className="text-xl font-semibold">{selectedImage.title}</h3>
              <p className="text-white/70">{selectedImage.description}</p>
              <p className="text-white/50 text-sm mt-2">
                {currentIndex + 1} / {galleryImages.length}
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default GallerySection;
